import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  CircularProgress
} from '@material-ui/core';
import {
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Visibility as PreviewIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { getLogos, uploadSettingMedia } from '../../services/settings.api';
import { useWebInfo } from '../../context/WebInfoContext';

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(2),
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(3),
    textAlign: "center"
  },
  // ✅ CORREGIDO: LogoRow dinámico según tema
  logoRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
    borderRadius: "8px",
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`
  },
  // ✅ CORREGIDO: LogoLabel dinámico según tema
  logoLabel: {
    fontWeight: "bold",
    color: theme.palette.text.primary,
    minWidth: "200px",
    fontSize: "14px"
  },
  // ✅ CORREGIDO: UrlField dinámico según tema
  urlField: {
    flex: 1,
    marginRight: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      borderRadius: "6px",
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
    },
  },
  actionButton: {
    minWidth: "40px",
    height: "40px",
    marginLeft: theme.spacing(1)
  },
  uploadButton: {
    backgroundColor: "#007bff",
    color: "white",
    "&:hover": {
      backgroundColor: "#0056b3"
    }
  },
  previewButton: {
    backgroundColor: "#28a745",
    color: "white",
    "&:hover": {
      backgroundColor: "#1e7e34"
    }
  },
  refreshButton: {
    backgroundColor: theme.mode === 'dark' ? '#6b7280' : "#6c757d",
    color: "white",
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? '#4b5563' : "#545b62"
    }
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4)
  },
  // ✅ CORREGIDO: ErrorMessage dinámico según tema
  errorMessage: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.mode === 'dark' ? '#7f1d1d' : "#f8d7da",
    color: theme.mode === 'dark' ? '#fca5a5' : "#721c24",
    padding: theme.spacing(1),
    borderRadius: "4px",
    border: `1px solid ${theme.mode === 'dark' ? '#991b1b' : '#f5c6cb'}`,
    textAlign: "center"
  },
  // ✅ CORREGIDO: SuccessMessage dinámico según tema
  successMessage: {
    backgroundColor: theme.mode === 'dark' ? '#14532d' : "#d4edda",
    color: theme.mode === 'dark' ? '#86efac' : "#155724",
    padding: theme.spacing(1),
    borderRadius: "4px",
    marginBottom: theme.spacing(2),
    textAlign: "center"
  }
}));

const LogoManager = () => {
  const classes = useStyles();
  const { updateFavicon } = useWebInfo();
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});

  // ✅ CARGAR LOGOS AL INICIALIZAR
  useEffect(() => {
    loadLogos();
  }, []);

  const loadLogos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getLogos();
      
      if (response.data.success) {
        setLogos(response.data.logos);
      } else {
        setError('Error al cargar los logos');
      }
    } catch (err) {
      console.error('Error cargando logos:', err);
      setError('Error de conexión al cargar los logos');
    } finally {
      setLoading(false);
    }
  };

  // ✅ ACTUALIZAR URL DE LOGO
  const updateLogoUrl = async (logoType, newUrl) => {
    try {
      setUpdating(prev => ({ ...prev, [logoType]: true }));
      
      // Por ahora solo actualizamos el estado local
      // En el futuro se podría implementar un endpoint PUT para actualizar URLs
      setLogos(prevLogos => 
        prevLogos.map(logo => 
          logo.type === logoType 
            ? { ...logo, url: newUrl }
            : logo
        )
      );
      
      // ✅ Si es favicon, actualizar dinámicamente en el navegador
      if (logoType === 'favicon' && newUrl) {
        updateFavicon(newUrl);
        toast.info('Favicon actualizado en el navegador');
      }
      
      toast.success(`URL de ${logoType} actualizada`);
    } catch (err) {
      console.error('Error actualizando logo:', err);
      toast.error('Error al actualizar la URL');
    } finally {
      setUpdating(prev => ({ ...prev, [logoType]: false }));
    }
  };

  // ✅ ABRIR UPLOAD DE ARCHIVO
  const handleUpload = (logoType) => {
    // Crear input file temporal
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/x-icon,image/svg+xml';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        setUpdating(prev => ({ ...prev, [logoType]: true }));
        
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await uploadSettingMedia(logoType, formData);
        
        if (response.data.mensagem === 'Arquivo Anexado') {
          toast.success(`Logo ${logoType} actualizado exitosamente`);
          // Recargar logos para obtener las nuevas URLs
          await loadLogos();
          
          // ✅ Si es favicon, actualizar dinámicamente en el navegador
          if (logoType === 'favicon') {
            const logosResponse = await getLogos();
            if (logosResponse.data?.success && logosResponse.data?.logos) {
              const faviconLogo = logosResponse.data.logos.find(logo => logo.type === 'favicon');
              if (faviconLogo && faviconLogo.url) {
                updateFavicon(faviconLogo.url);
                toast.info('Favicon actualizado en el navegador');
              }
            }
          }
        }
      } catch (err) {
        console.error('Error subiendo archivo:', err);
        toast.error('Error al subir el archivo');
      } finally {
        setUpdating(prev => ({ ...prev, [logoType]: false }));
      }
    };
    
    input.click();
  };

  // ✅ PREVIEW DEL LOGO
  const handlePreview = (logoUrl) => {
    window.open(logoUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
        <Typography style={{ marginLeft: 16 }}>Cargando logos...</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.errorMessage}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
        <Button onClick={loadLogos} style={{ marginLeft: 16 }}>
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>
        {i18n.t("uploader.title")}
      </Typography>

      {logos.map((logo) => (
        <div key={logo.type} className={classes.logoRow}>
          <Typography className={classes.logoLabel}>
            {logo.description}:
          </Typography>
          
          <TextField
            className={classes.urlField}
            variant="outlined"
            size="small"
            value={logo.url}
            onChange={(e) => updateLogoUrl(logo.type, e.target.value)}
            placeholder="URL del logo"
            disabled={updating[logo.type]}
          />
          
          <Tooltip title="Subir archivo">
            <IconButton
              className={`${classes.actionButton} ${classes.uploadButton}`}
              onClick={() => handleUpload(logo.type)}
              disabled={updating[logo.type]}
            >
              {updating[logo.type] ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <UploadIcon />
              )}
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Vista previa">
            <IconButton
              className={`${classes.actionButton} ${classes.previewButton}`}
              onClick={() => handlePreview(logo.url)}
            >
              <PreviewIcon />
            </IconButton>
          </Tooltip>
        </div>
      ))}

      <Box style={{ textAlign: "center", marginTop: 24 }}>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={async () => {
            await loadLogos();
            // ✅ Actualizar favicon después de recargar logos
            try {
              const logosResponse = await getLogos();
              if (logosResponse.data?.success && logosResponse.data?.logos) {
                const faviconLogo = logosResponse.data.logos.find(logo => logo.type === 'favicon');
                if (faviconLogo && faviconLogo.url) {
                  updateFavicon(faviconLogo.url);
                }
              }
            } catch (err) {
              console.warn('Error actualizando favicon:', err);
            }
          }}
          className={classes.refreshButton}
        >
          Actualizar Lista
        </Button>
      </Box>
    </div>
  );
};

export default LogoManager;
